import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Grid,
    TextField,
} from "@mui/material";
import { Formik } from "formik";
import { useState } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

import DefaultApiService from "../../../../../infrastructure/api/default";
import { wrapFormikHandleChange } from "../../../../../infrastructure/utils/formatters";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import DropdownPicker from "../../../../components/shared/dropdown";
import JalaliDateTimePicker from "../../../../components/shared/jalali-datetimepicker";

const validationSchema = Yup.object().shape({
  feederName: Yup.string().required("لطفا این فیلد را پر کنید."),
  substationId: Yup.number().required("لطفا این فیلد را پر کنید."),
  transformationId: Yup.string().required("لطفا این فیلد را پر کنید."),
  feederIGMCCode: Yup.string().required("لطفا این فیلد را پر کنید."),
  feederGeoCode: Yup.string().required("لطفا این فیلد را پر کنید."),
  ownerCompanyId: Yup.number().required("لطفا این فیلد را پر کنید."),
  ownerCityId: Yup.number().required("لطفا این فیلد را پر کنید."),
  maxRange: Yup.number().required("لطفا این فیلد را پر کنید."),
  minRange: Yup.number().required("لطفا این فیلد را پر کنید."),
  percentAgriculture: Yup.number().required("لطفا این فیلد را پر کنید."),
  percentHouseholds: Yup.number().required("لطفا این فیلد را پر کنید."),
  percentIndustrial: Yup.number().required("لطفا این فیلد را پر کنید."),
  percentPublic: Yup.number().required("لطفا این فیلد را پر کنید."),
  percentOther: Yup.number().required("لطفا این فیلد را پر کنید."),
  ownershipKind: Yup.string().required("لطفا این فیلد را پر کنید."),
  feederKind: Yup.string().required("لطفا این فیلد را پر کنید."),
  feederActDate: Yup.string().required("لطفا این فیلد را پر کنید."),
  feederDismandDate: Yup.string().nullable(),
});

const FeederModifyPage = () => {
  const data = useLoaderData("root");
  let navigate = useNavigate();
  const { id: roleId } = useParams();

  const [loading, setLoading] = useState(false);

  const onNavigateToListPage = () => {
    navigate("/BI/Grid/FeederInformation");
  };

  const onFilterByCompanyId = (list, id) => {
    return list.filter((x) => x.companyId === id);
  };

  const onFilterBySubstationId = (list, id) => {
    return list.filter((x) => x.substationId === id);
  };

  // Submit Action
  const onSubmit = async (formValue, { setSubmitting }) => {
    setLoading(true);
    const sendData = { ...data, ...formValue };

    try {
      const res = await DefaultApiService.modifyApi(
        "BI/Grid/FeederInformation",
        sendData
      );

      if (res.isSuccess) {
        toast.success(res.message);
        onNavigateToListPage();
      } else {
        toast.error(res.message);
      }

      setLoading(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || "خطایی رخ داده است.");

      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title={
              roleId !== "create"
                ? `ویرایش ${data?.feeder?.feederName}`
                : "ایجاد"
            }
          />

          <CardContent>
            <Formik
              initialValues={data.feeder}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
              }) => {
                const onMakeFormikVariable = (name) => ({
                  name: name,
                  fullWidth: true,
                  value: values[name],
                  error: errors[name] && touched[name],
                  helperText: errors[name] && touched[name] && errors[name],
                  variant: "outlined",
                });

                return (
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="نام فیدر"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("feederName")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <DropdownPicker
                          label="ایستگاه"
                          onChange={(event) => {
                            setFieldValue("substationId", event.target.value);
                          }}
                          list={data.substationList}
                          optionValue="id"
                          optionName="substationName"
                          {...onMakeFormikVariable("substationId")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <DropdownPicker
                          label="ترانس"
                          onChange={(event) => {
                            setFieldValue(
                              "transformationId",
                              event.target.value
                            );
                          }}
                          list={onFilterBySubstationId(
                            data.transformationList,
                            values.substationId
                          )}
                          optionValue="id"
                          optionName="transNumber"
                          {...onMakeFormikVariable("transformationId")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="کد IGMC"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("feederIGMCCode")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="کد GIS"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("feederGeoCode")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <DropdownPicker
                          label="مالکیت(شرکت)"
                          onChange={(event) => {
                            setFieldValue("ownerCompanyId", event.target.value);
                            setFieldValue("ownerCityId", "");
                          }}
                          list={data.companyList}
                          optionName="companyName"
                          optionValue="id"
                          {...onMakeFormikVariable("ownerCompanyId")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <DropdownPicker
                          label="مالکیت(شهرستان)"
                          onChange={(event) => {
                            setFieldValue("ownerCityId", event.target.value);
                          }}
                          list={onFilterByCompanyId(
                            data.cityList,
                            values.ownerCompanyId
                          )}
                          optionName="cityName"
                          optionValue="id"
                          {...onMakeFormikVariable("ownerCityId")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="بیشترین حد مجاز(MW)"
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("maxRange")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="کمترین حد مجاز(MW)"
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("minRange")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="درصد تعرفه خانگی"
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("percentHouseholds")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="درصد تعرفه صنعتی"
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("percentIndustrial")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="درصد تعرفه کشاورزی"
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("percentAgriculture")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="درصد تعرفه عمومی"
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("percentPublic")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="درصد تعرفه سایر"
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("percentOther")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <DropdownPicker
                          label="نوع مالکیت"
                          onChange={(event) => {
                            setFieldValue("ownershipKind", event.target.value);
                          }}
                          list={data.ownershipKind}
                          {...onMakeFormikVariable("ownershipKind")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <DropdownPicker
                          label="نوع فیدر"
                          onChange={(event) => {
                            setFieldValue("feederKind", event.target.value);
                          }}
                          list={data.feederKind}
                          {...onMakeFormikVariable("feederKind")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <JalaliDateTimePicker
                          label="تاریخ شروع بهره برداری"
                          onChange={(value) => {
                            setFieldValue("feederActDate", value);
                          }}
                          {...onMakeFormikVariable("feederActDate")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <JalaliDateTimePicker
                          label="تاریخ پایان بهره برداری"
                          onChange={(value) => {
                            setFieldValue("feederDismandDate", value);
                          }}
                          {...onMakeFormikVariable("feederDismandDate")}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={12}
                        container
                        justifyContent={"flex-end"}
                      >
                        <Button
                          disabled={isSubmitting}
                          variant="outlined"
                          size="large"
                          color="gray"
                          sx={{ mx: 1 }}
                          onClick={onNavigateToListPage}
                        >
                          انصراف
                        </Button>

                        <Button
                          disabled={isSubmitting}
                          variant="contained"
                          type="submit"
                          size="large"
                        >
                          {loading && (
                            <CircularProgress size={16} color="inherit" />
                          )}
                          {roleId !== "create" ? "ویرایش" : "ایجاد"}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                );
              }}
            </Formik>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default FeederModifyPage;
